import { useState } from 'react'
import { Link2, Plus, VideoOff } from 'lucide-react'

import type { Camera } from '@/components/cameras/camera-types'
import type { CameraTestResult } from '@/lib/api/cameras-api'
import { AttachCameraDialog } from '@/components/cameras/attach-camera-dialog'
import { CameraCard } from '@/components/cameras/camera-card'
import { CameraFormDialog } from '@/components/cameras/camera-form-dialog'
import { CameraLiveDialog } from '@/components/cameras/camera-live-dialog'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import {
    useGrowerAreaCameras,
    useSaveCamera,
    useSetCameraLocation,
    useTestCamera,
} from '@/hooks/use-cameras-query'
import { useGrowerCameraSettings } from '@/hooks/use-grower-camera-settings-query'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** Vùng trồng ĐÃ LƯU. Vùng chưa có id thì đừng render panel này. */
    areaId: string
    /** Chiều cao khung, khớp với bản đồ để đổi tab không giật layout. */
    height: number
    /**
     * Màn này cho sửa (màn sửa hộ) hay chỉ xem (trang xem hộ). Sửa/gắn/gỡ
     * camera còn cần cameras.write, xoay camera cần cameras.control.
     */
    canWrite: boolean
}

/**
 * Lưới camera của MỘT vùng trồng, hiển thị ở đúng chỗ bản đồ khi người dùng
 * chuyển sang tab "Camera". Camera là module riêng: ở đây chỉ thêm camera mới
 * cho vùng, gắn camera có sẵn, hoặc gỡ camera khỏi vùng — xoá hẳn camera làm ở
 * màn Danh sách camera.
 *
 * Mỗi vùng chỉ 2–3 camera nên thẻ phát luồng SỐNG (khác màn tổng hợp dùng ảnh
 * tĩnh). Mở chi tiết một camera là ngắt toàn bộ luồng trong lưới, chỉ giữ đúng
 * một kết nối trong hộp thoại; đóng hộp thoại thì lưới kết nối lại. Rời tab là
 * unmount, đóng luôn mọi kết nối.
 */
export function AreaCameraPanel({ areaId, height, canWrite }: Props) {
    const { hasPermission } = useAuth()
    if (!hasPermission('cameras.read')) {
        return (
            <div
                className="flex items-center justify-center rounded-md border border-dashed bg-muted/40 px-6 text-center text-sm text-muted-foreground"
                style={{ height }}
            >
                {m.camera_permission_denied()}
            </div>
        )
    }
    return (
        <AreaCameraGrid
            areaId={areaId}
            height={height}
            canWrite={canWrite && hasPermission('cameras.write')}
            canControl={canWrite && hasPermission('cameras.control')}
        />
    )
}

function AreaCameraGrid({
    areaId,
    height,
    canWrite,
    canControl,
}: Props & { canControl: boolean }) {
    const { data: settings } = useGrowerCameraSettings()
    const { data: cameras = [], isLoading } = useGrowerAreaCameras(areaId)

    const save = useSaveCamera()
    const place = useSetCameraLocation()
    const test = useTestCamera()

    const [formOpen, setFormOpen] = useState(false)
    const [attachOpen, setAttachOpen] = useState(false)
    const [editing, setEditing] = useState<Camera | null>(null)
    // Camera đang xem chi tiết. Còn trong danh sách = mọi luồng trong lưới bị
    // ngắt, chỉ còn đúng một luồng trong hộp thoại. Camera bị xoá ở nơi khác
    // rồi danh sách tải lại thì hộp thoại tự đóng — lưới cũng phải nối lại theo,
    // không kẹt "tạm ngắt" khi chẳng còn hộp thoại nào.
    const [liveId, setLiveId] = useState<string | null>(null)
    const detailOpen = cameras.some((c) => c.id === liveId)
    const [testResult, setTestResult] = useState<CameraTestResult | null>(null)

    const openAdd = () => {
        setEditing(null)
        setTestResult(null)
        setFormOpen(true)
    }
    const openEdit = (camera: Camera) => {
        setEditing(camera)
        setTestResult(null)
        setFormOpen(true)
    }
    const closeForm = () => {
        setFormOpen(false)
        setEditing(null)
        setTestResult(null)
    }

    const handleUnplace = (camera: Camera) => {
        // Chỉ gỡ khỏi vùng, camera vẫn còn ở màn Danh sách camera để gắn lại.
        if (!window.confirm(m.camera_unplace_confirm({ name: camera.name }))) {
            return
        }
        void place
            .mutateAsync({ cameraId: camera.id, growerAreaId: null })
            .catch(() => undefined)
    }

    const integrationOff = settings ? !settings.enabled : false

    return (
        <div className="space-y-3">
            {integrationOff && (
                <div className="rounded-md border border-dashed bg-muted/40 px-3 py-2">
                    <p className="text-sm font-medium">
                        {m.grower_area_camera_integration_off()}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {m.grower_area_camera_integration_off_hint()}
                    </p>
                </div>
            )}

            {isLoading ? (
                <div
                    className="flex items-center justify-center rounded-md border border-dashed bg-muted/40 text-sm text-muted-foreground"
                    style={{ height }}
                >
                    {m.common_loading()}
                </div>
            ) : cameras.length === 0 ? (
                <EmptyState
                    height={height}
                    canWrite={canWrite}
                    onAdd={openAdd}
                    onAttach={() => setAttachOpen(true)}
                />
            ) : (
                <div
                    className="grid content-start gap-3 overflow-y-auto sm:grid-cols-2"
                    style={{ maxHeight: height }}
                >
                    {cameras.map((camera) => (
                        <CameraCard
                            key={camera.id}
                            camera={camera}
                            // Mỗi vùng chỉ 2–3 camera nên lưới phát luồng
                            // sống; mở chi tiết thì ngắt hết để chỉ còn một.
                            active={!detailOpen}
                            mode="live"
                            canWrite={canWrite}
                            onLive={(cam) => setLiveId(cam.id)}
                            onEdit={openEdit}
                            onRemove={handleUnplace}
                            removeMode="unplace"
                        />
                    ))}
                    {canWrite && (
                        <div className="flex min-h-[190px] flex-col items-center justify-center gap-2 rounded-md border border-dashed p-4 text-center">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={openAdd}
                            >
                                <Plus className="size-4" />
                                {m.grower_area_camera_add_for_area()}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setAttachOpen(true)}
                            >
                                <Link2 className="size-4" />
                                {m.camera_attach_open()}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            <CameraFormDialog
                open={formOpen}
                camera={editing}
                isSaving={save.isPending}
                isTesting={test.isPending}
                testResult={testResult}
                onSave={async (values) => {
                    await save.mutateAsync({
                        cameraId: editing?.id,
                        // Thêm mới từ tab của vùng thì gắn luôn vào vùng này.
                        growerAreaId: editing ? undefined : areaId,
                        data: values,
                    })
                    closeForm()
                }}
                onTest={() => {
                    if (!editing) return
                    void test
                        .mutateAsync(editing.id)
                        .then(setTestResult)
                        .catch(() => setTestResult(null))
                }}
                onClose={closeForm}
            />

            <CameraLiveDialog
                cameras={cameras}
                cameraId={liveId}
                onCameraChange={setLiveId}
                canControl={canControl}
            />

            <AttachCameraDialog
                open={attachOpen}
                isHere={(c) => c.grower_area?.area_id === areaId}
                description={m.camera_attach_desc()}
                submitLabel={m.camera_attach_submit()}
                isSaving={place.isPending}
                onAttach={(cameraId) =>
                    place.mutateAsync({ cameraId, growerAreaId: areaId })
                }
                onClose={() => setAttachOpen(false)}
            />
        </div>
    )
}

function EmptyState({
    height,
    canWrite,
    onAdd,
    onAttach,
}: {
    height: number
    canWrite: boolean
    onAdd: () => void
    onAttach: () => void
}) {
    return (
        <div
            className="flex flex-col items-center justify-center gap-1.5 rounded-md border border-dashed bg-muted/40 px-6 text-center"
            style={{ height }}
        >
            <VideoOff className="size-6 text-muted-foreground" />
            <p className="text-sm font-medium">
                {m.grower_area_camera_empty_title()}
            </p>
            <p className="max-w-[34ch] text-xs text-muted-foreground">
                {m.grower_area_camera_empty_hint()}
            </p>
            {canWrite && (
                <div className="mt-1 flex flex-wrap justify-center gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onAdd}
                    >
                        <Plus className="size-4" />
                        {m.grower_area_camera_add()}
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onAttach}
                    >
                        <Link2 className="size-4" />
                        {m.camera_attach_open()}
                    </Button>
                </div>
            )}
        </div>
    )
}
